import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { Link, useQueryParams } from 'raviger';
import { Button, ButtonGroup, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft
} from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Select from 'react-select';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import AnimalRoomAssignmentCard from '../components/AnimalRoomAssignmentCard';
import { SystemErrorContext } from '../components/SystemError';

const customStyles = {
  // For the select it self, not the options of the select
  control: (styles, { isDisabled}) => {
    return {
      ...styles,
      color: '#FFF',
      cursor: isDisabled ? 'not-allowed' : 'default',
      backgroundColor: isDisabled ? '#DFDDDD' : 'white',
      // height: 35,
      // minHeight: 35
    }
  },
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#595959' : 'black'
  }),
  menu: provided  => ({ ...provided, zIndex: 9999 })
};

function ShelterRoomAssignment({ id, incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    building_id = null,
  } = queryParams;

  const [data, setData] = useState({
    name: '',
    address: '',
    full_address: '',
    city: '',
    state: '',
    zip_code: '',
    description: '',
    image: '',
    buildings: [],
    rooms: [],
    action_history: [],
    unroomed_animals: [],
    animal_count: 0,
  });

  const [selectedBuilding, setSelectedBuilding] = useState(Number(building_id));
  const [options, setOptions] = useState([]);

  function handleOnDragEnd(result) {

    const { destination, source, draggableId } = result;

    // Bail if invalid destination.
    if (!destination) return;

    // Bail if no changes.
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Ordering within same room.
    if (source.droppableId === destination.droppableId) {
      let animals = [];
      if (source.droppableId === 'unroomed_animals') {
        animals = Array.from(data.unroomed_animals);
        const [reorderedItem] = animals.splice(source.index, 1);
        animals.splice(destination.index, 0, reorderedItem);
        setData(prevState => ({ ...prevState, 'unroomed_animals':animals }));
      }
      else {
        animals = Array.from(data.rooms[source.droppableId].animals);
        const [reorderedItem] = animals.splice(source.index, 1);
        animals.splice(destination.index, 0, reorderedItem);
        let rooms = Array.from(data.rooms);
        rooms[source.droppableId].animals = animals;
        setData(prevState => ({ ...prevState, 'rooms':rooms }));
      }
      axios.patch('/animals/api/animal/' + Number(draggableId) + '/', {set_order:destination.index})
      .catch(error => {
        setShowSystemError(true);
      });
    }
    else {
      let source_animals = [];
      let dest_animals = [];
      let rooms = Array.from(data.rooms);
      let unroomed_animals = Array.from(data.unroomed_animals);
      // Unroomed to room.
      if (source.droppableId === 'unroomed_animals') {
        dest_animals = Array.from(data.rooms[destination.droppableId].animals);
        const [reorderedItem] = unroomed_animals.splice(source.index, 1);
        dest_animals.splice(destination.index, 0, reorderedItem);
        rooms[destination.droppableId].animals = dest_animals;
        axios.patch('/animals/api/animal/' + Number(draggableId) + '/', {room:data.rooms[destination.droppableId].id, set_order:destination.index})
        .catch(error => {
          setShowSystemError(true);
        });
      }
      // Room to unroomed.
      else if (destination.droppableId === 'unroomed_animals') {
        source_animals = Array.from(data.rooms[source.droppableId].animals);
        const [reorderedItem] = source_animals.splice(source.index, 1);
        unroomed_animals.splice(destination.index, 0, reorderedItem);
        rooms[source.droppableId].animals = source_animals;
        axios.patch('/animals/api/animal/' + Number(draggableId) + '/', {room:null, set_order:destination.index})
        .catch(error => {
          setShowSystemError(true);
        });
      }
      // Room to room.
      else {
        dest_animals = Array.from(data.rooms[destination.droppableId].animals);
        source_animals = Array.from(data.rooms[source.droppableId].animals);
        const [reorderedItem] = source_animals.splice(source.index, 1);
        dest_animals.splice(destination.index, 0, reorderedItem);
        rooms[destination.droppableId].animals = dest_animals;
        rooms[source.droppableId].animals = source_animals.filter(animal => animal.id !== Number(draggableId));
        axios.patch('/animals/api/animal/' + Number(draggableId) + '/', {room:data.rooms[destination.droppableId].id, set_order:destination.index})
        .catch(error => {
          setShowSystemError(true);
        });
      }
      setData(prevState => ({ ...prevState, 'rooms':rooms, 'unroomed_animals':unroomed_animals }));
    }
  }

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchShelterData = async () => {
      // Fetch Shelter Details data.
      await axios.get('/shelter/api/shelter/' + id + '/?room_assign=true&incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let building_options = [];
          let rooms = [];
          response.data.buildings.forEach(function(building){
            rooms = rooms.concat(building.rooms);
            building_options.push({value:building.id, label:building.name})
          });
          response.data['rooms'] = rooms;
          setData(response.data);
          setOptions(building_options);
          if (!building_id && response.data.buildings.length > 0) {
            setSelectedBuilding(response.data.buildings[0].id);
          }
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchShelterData();
    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, building_id, incident]);

  return (
    <>
      <Header>
        <span style={{cursor:'pointer'}} onClick={() => window.history.back()} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="sm" inverse /></span>
        {data.name}
        &nbsp;- Room Animals
      </Header>
      <h4 style={{marginBottom:"1px"}}>Buildings</h4>
      <Row className="d-flex gx-2 mt-1 mb-3">
        <Col xs={6}>
          {options.length > 0 ? <Select
            id="status"
            type="text"
            name="status"
            options={options}
            styles={customStyles}
            value={options.length ? options.filter(option => option.value === selectedBuilding)[0] : null}
            onChange={(instance) => {
              setSelectedBuilding(instance.value)
            }}
          /> : ""}
        </Col>
      </Row>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Row className="mb-3 d-flex" style={{height:"100px"}}>
          <div className="col mt-1">
            <h4 style={{marginBottom:"3px"}}>Roomless Animals</h4>
            <Droppable droppableId="unroomed_animals" direction="horizontal">
              {(provided, snapshot) => (
              <Card className="border rounded" style={{display:"flex", justifyContent:"space-around", backgroundColor:snapshot.isDraggingOver ? "gray" : "#303030"}}>
                <Scrollbar horizontal="true" no_shadow="true" style={{height:"89px", width:"99.99%", shadowheight:"89px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-18px", marginRight:"15px", overflowX: "auto", overflowY:"hidden"}}/>} renderThumbVertical={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <Card.Body style={{paddingBottom:"3px", marginBottom:"-10px", display:"flex", flexDirection:"column"}}>
                    <ul className="unroomed_animals" {...provided.droppableProps} ref={provided.innerRef}>
                    {data.unroomed_animals.map((animal, index) => (
                      <Draggable key={animal.id} draggableId={String(animal.id)} index={index}>
                        {(provided, snapshot) => (
                          <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <AnimalRoomAssignmentCard animal={animal} snapshot={snapshot} direction="horizontal" />
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    </ul>
                  </Card.Body>
                </Scrollbar>
              </Card>
              )}
            </Droppable>
          </div>
        </Row>
        <h4 style={{marginBottom:"0px", marginTop:"17px"}}>Rooms</h4>
        <Scrollbar style={{height:"536px"}} no_shadow="true" renderView={props => <div {...props} style={{...props.style, overflowX:"hidden", overflowY:"scroll", marginBottom:"0px"}}/>} renderThumbHorizontal={props => <div {...props} style={{...props.style, display:"none"}} />}>
        <Row className="d-flex" style={{marginTop:"5px", marginLeft:"-10px"}}>
          {data.rooms.map((room, index) => (
            <span key={room.id} className="col me-3" xs={1} hidden={room.building !== selectedBuilding} style={{marginBottom:"15px", maxWidth:"190px"}}>
                <Droppable droppableId={String(index)}>
                  {(provided, snapshot) => (
                  <Card className="border rounded me-3 animals" style={{width:"190px", minHeight: "251px", height: "251px", display:"flex", overflowX:"hidden", overflowY:"auto", backgroundColor:snapshot.isDraggingOver ? "gray" : "#303030"}} {...provided.droppableProps} ref={provided.innerRef}>
                    <div style={{width:"190px"}}>
                      <div className="card-header border d-flex align-items-center ps-1" title={room.name} style={{height:"37px", marginTop:"-1px", width:"100%", backgroundColor:"#615e5e", fontSize:"16px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                        <Link href={"/" + organization + "/" + incident + "/shelter/room/" + room.id} className="text-link float-left" style={{textDecoration:"none", color:"white"}}>{room.name}</Link>
                      </div>
                    </div>
                    <Scrollbar style={{height:"213px", marginRight:"-1px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-36px"}}/>}>
                      <Card.Body style={{marginTop:"-3px", display:"flex", flexDirection:"column"}}>
                      {room.animals.map((animal, index) => (
                        <Draggable key={animal.id} draggableId={String(animal.id)} index={index}>
                          {(provided, snapshot) => (
                            <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <AnimalRoomAssignmentCard animal={animal} snapshot={snapshot} />
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      </Card.Body>
                    </Scrollbar>
                  </Card>
                  )}
                </Droppable>
            </span>
          ))}
          {data.rooms.filter(room => room.building === selectedBuilding).length < 1 ? <span style={{marginTop:"5px"}}>This building does not have any rooms yet.</span> : ""}
        </Row>
        </Scrollbar>
      </DragDropContext>
    </>
  );
};

export default ShelterRoomAssignment;
